
import React, { useState, useEffect } from 'react';
import { X, Bell, Camera, CreditCard, AlertTriangle, FileBarChart } from 'lucide-react';
import { Project } from '../types';

interface SmartNotificationProps {
  project: Project;
  onAddExpense: () => void;
  onTakePhoto: () => void;
  onViewReports: () => void;
}

type NotificationType = 'expense_reminder' | 'photo_reminder' | 'budget_alert' | 'report_reminder' | null;

export const SmartNotification: React.FC<SmartNotificationProps> = ({ 
  project, 
  onAddExpense, 
  onTakePhoto,
  onViewReports
}) => {
  const [isVisible, setIsVisible] = useState(false);
  const [type, setType] = useState<NotificationType>(null);

  useEffect(() => {
    if (!project || !project.id) return;

    const checkNotifications = () => {
      const today = new Date();
      const lastDismissed = localStorage.getItem(`notification_dismissed_${project.id}`);
      
      // Se já dispensou hoje, não mostra nada
      if (lastDismissed) {
        const dismissedDate = new Date(lastDismissed);
        if (dismissedDate.getDate() === today.getDate() && dismissedDate.getMonth() === today.getMonth()) {
          return;
        }
      }

      // 1. Alerta de Orçamento (Prioridade Alta)
      const totalSpent = project.expenses.reduce((acc, curr) => acc + curr.amount, 0);
      const budget = project.initialBudget || 1;
      const usage = totalSpent / budget;

      if (usage > 0.9 && usage < 1.0) {
        setType('budget_alert');
        setIsVisible(true);
        return;
      }

      // 2. Lembrete de Gastos (Se > 3 dias sem lançar)
      // Ordena despesas por data para pegar a mais recente
      const sortedExpenses = [...project.expenses].sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());
      const lastExpenseDate = sortedExpenses.length > 0 ? new Date(sortedExpenses[0].date) : new Date(project.startDate);
      const daysSinceExpense = Math.floor((today.getTime() - lastExpenseDate.getTime()) / (1000 * 60 * 60 * 24));

      if (daysSinceExpense > 3) {
        setType('expense_reminder');
        setIsVisible(true);
        return;
      }

      // 3. Lembrete de Fotos (Se > 7 dias sem foto)
      const sortedPhotos = [...project.photos].sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());
      const lastPhotoDate = sortedPhotos.length > 0 ? new Date(sortedPhotos[0].date) : new Date(project.startDate);
      const daysSincePhoto = Math.floor((today.getTime() - lastPhotoDate.getTime()) / (1000 * 60 * 60 * 24));

      if (daysSincePhoto > 7) {
        setType('photo_reminder');
        setIsVisible(true);
        return;
      }

      // 4. Lembrete de Relatório (Fim do mês)
      const isEndOfWeek = today.getDay() === 5; // Sexta-feira
      if (isEndOfWeek) {
         setType('report_reminder');
         setIsVisible(true);
         return;
      }
    };

    // Pequeno delay para não aparecer instantaneamente ao carregar
    const timer = setTimeout(checkNotifications, 2000);
    return () => clearTimeout(timer);
  }, [project]);

  const handleDismiss = () => {
    setIsVisible(false);
    localStorage.setItem(`notification_dismissed_${project.id}`, new Date().toISOString());
  };

  const handleAction = () => {
    setIsVisible(false);
    // Não salvamos no localStorage para permitir que o alerta apareça novamente caso a ação não seja concluída futuramente
    if (type === 'expense_reminder') onAddExpense();
    if (type === 'photo_reminder') onTakePhoto();
    if (type === 'report_reminder') onViewReports();
    if (type === 'budget_alert') onViewReports();
  };

  if (!isVisible || !type) return null;

  const content = {
    expense_reminder: {
      icon: CreditCard,
      title: "Mantenha o controle!",
      text: "Faz alguns dias que você não registra gastos. Comprou algo recentemente?",
      button: "Registrar Gasto",
      color: "bg-blue-500",
      bg: "bg-blue-50 dark:bg-blue-900/20",
      border: "border-blue-100 dark:border-blue-800"
    },
    photo_reminder: {
      icon: Camera,
      title: "Diário de Obra",
      text: "A obra mudou desde a última foto. Que tal registrar o progresso hoje?",
      button: "Tirar Foto",
      color: "bg-emerald-500",
      bg: "bg-emerald-50 dark:bg-emerald-900/20",
      border: "border-emerald-100 dark:border-emerald-800"
    },
    budget_alert: {
      icon: AlertTriangle,
      title: "Atenção ao Orçamento",
      text: "Você já utilizou mais de 90% do orçamento previsto. Revise seus números.",
      button: "Ver Relatório",
      color: "bg-amber-500",
      bg: "bg-amber-50 dark:bg-amber-900/20",
      border: "border-amber-100 dark:border-amber-800"
    },
    report_reminder: {
      icon: FileBarChart,
      title: "Fechamento Semanal",
      text: "Sexta-feira é um ótimo dia para gerar um relatório e enviar aos envolvidos.",
      button: "Gerar PDF",
      color: "bg-purple-500",
      bg: "bg-purple-50 dark:bg-purple-900/20",
      border: "border-purple-100 dark:border-purple-800"
    }
  }[type];

  return (
    <div className="fixed top-20 right-4 sm:right-10 z-[90] max-w-sm w-full animate-fadeIn md:max-w-md">
      <div className={`relative p-5 rounded-2xl shadow-[0_8px_30px_rgb(0,0,0,0.12)] backdrop-blur-md border ${content.bg} ${content.border} flex items-start gap-4`}>
        <button 
          onClick={handleDismiss} 
          className="absolute top-2 right-2 p-1.5 text-slate-400 hover:text-slate-600 dark:hover:text-slate-200 transition-colors rounded-full"
        >
          <X size={14} />
        </button>

        <div className={`shrink-0 size-12 rounded-xl flex items-center justify-center text-white shadow-lg ${content.color}`}>
          <content.icon size={24} strokeWidth={2.5} />
        </div>

        <div className="flex-1 pt-1">
          <h4 className="text-sm font-black text-slate-900 dark:text-white uppercase tracking-tight mb-1">
            {content.title}
          </h4>
          <p className="text-xs font-medium text-slate-600 dark:text-slate-300 leading-relaxed mb-3">
            {content.text}
          </p>
          <button 
            onClick={handleAction}
            className={`px-4 py-2 rounded-lg text-[10px] font-black uppercase tracking-widest text-white shadow-md hover:opacity-90 transition-opacity ${content.color}`}
          >
            {content.button}
          </button>
        </div>
      </div>
    </div>
  );
};
